/**
 * ElementPickerDialog Component
 *
 * Dialog for selecting an element from the user's library.
 * Shows elements filtered by type (outfit, prop, scene).
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { Shirt, Package, Image } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePhotostudioElements } from '@/hooks/usePhotostudioElements';
import type { IElementPickerDialogProps, ElementType } from '@/@types/photostudio';

const TYPE_CONFIG: Record<ElementType, { label: string; icon: typeof Shirt }> = {
  outfit: { label: 'Outfits', icon: Shirt },
  prop: { label: 'Props', icon: Package },
  scene: { label: 'Scenes', icon: Image },
};

export function ElementPickerDialog({
  isOpen,
  elementType,
  onClose,
  onSelect,
}: IElementPickerDialogProps) {
  const { elements, isLoading } = usePhotostudioElements();

  // Filter elements by type
  const filteredElements = elements.filter(e => e.type === elementType);

  const config = TYPE_CONFIG[elementType];
  const Icon = config.icon;

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="size-5" />
            Select {config.label.slice(0, -1)}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
          ) : filteredElements.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon className="mb-3 size-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                No {config.label.toLowerCase()} in your library
              </p>
              <p className="mt-1 text-sm text-muted-foreground/70">
                Upload an image directly or save one to your library
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-3 p-1">
              {filteredElements.map(element => (
                <Card
                  key={element._id}
                  className={cn(
                    'group cursor-pointer overflow-hidden transition-all hover:ring-2 hover:ring-primary'
                  )}
                  onClick={() => onSelect(element._id)}
                >
                  <div className="aspect-square overflow-hidden bg-muted">
                    {element.imageUrl ? (
                      <img
                        src={element.imageUrl}
                        alt={element.name}
                        className="size-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center">
                        <Icon className="size-8 text-muted-foreground/50" />
                      </div>
                    )}
                  </div>
                  <div className="p-2">
                    <p className="truncate text-sm font-medium">{element.name}</p>
                    {element.description && (
                      <p className="truncate text-xs text-muted-foreground">
                        {element.description}
                      </p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
